/**
 * Direct partner onboarding, for the BD-driven path, as a panel form.
 *
 * A partner created here skips the review: it lands `active` with a master
 * token already issued, which is why the token reveal follows immediately.
 * Closing the reveal opens the new partner's record in the same panel.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@optum/api-client/client';
import { createFederatedPartner } from '@optum/api-client/endpoints/federated-partners';
import type { FederatedPartnerWithSecret } from '@optum/api-client/types';

import { PanelFormActions, PanelFormFields, usePanelFormDone } from '../../components/shell/PanelForm.js';
import { Field } from '../../ui/Field.js';
import { Input, Typography } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useStepUp } from '../../lib/step-up.js';
import { PartnerTokenDialog } from './PartnerTokenDialog.js';

const IDP_NAME = /^[a-z][a-z0-9_-]{1,63}$/;

export function CreatePartnerForm() {
  const qc = useQueryClient();
  const toast = useToast();
  const done = usePanelFormDone();
  const [name, setName] = useState('');
  const [idpName, setIdpName] = useState('');
  const [description, setDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [issued, setIssued] = useState<FederatedPartnerWithSecret | null>(null);
  // The partner is created with its master token, so the agent asks for a fresh sign-in.
  const [guardedCreate, stepUpDialog] = useStepUp(createFederatedPartner);

  const create = useMutation({
    mutationFn: () =>
      guardedCreate({
        name: name.trim(),
        idp_name: idpName.trim(),
        description: description.trim() || null,
        contact_email: contactEmail.trim() || null,
      }),
    onSuccess: (out) => {
      toast.success(`Created ${out.name}`);
      qc.invalidateQueries({ queryKey: qk.federatedPartners() });
      setIssued(out);
    },
    onError: (e) => toast.error('Create failed', e instanceof ApiError ? e.message : String(e)),
  });

  const idpNameValid = IDP_NAME.test(idpName);
  const canSubmit = name.trim().length > 0 && idpNameValid;

  return (
    <>
      <PanelFormFields onSubmit={() => canSubmit && !create.isPending && create.mutate()}>
        <Typography variant="body-muted">
          The partner is created active and a master token is issued at once. Copy it from the
          next dialog and send it through a secure channel.
        </Typography>
        <Field id="cp-name" label="Name" required>
          <Input
            id="cp-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Finixt"
            autoFocus
            required
          />
        </Field>
        <Field
          id="cp-idp-name"
          label="idp_name"
          required
          hint="Lowercase letters, digits, _ and -, 2–64 characters. Example: finixt-clerk."
        >
          <Input
            id="cp-idp-name"
            value={idpName}
            onChange={(e) => setIdpName(e.target.value.toLowerCase())}
            placeholder="finixt-clerk"
            aria-invalid={idpName !== '' && !idpNameValid}
            required
          />
        </Field>
        <Field id="cp-description" label="Description">
          <Input
            id="cp-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Finixt CRM/ERP partner platform"
          />
        </Field>
        <Field id="cp-contact" label="Contact email">
          <Input
            id="cp-contact"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="ops@finixt.com"
          />
        </Field>
        <PanelFormActions
          submitLabel="Create partner"
          pending={create.isPending}
          disabled={!canSubmit}
          dirty={(name !== '' || idpName !== '' || description !== '' || contactEmail !== '') && issued === null}
        />
      </PanelFormFields>
      {stepUpDialog}
      <PartnerTokenDialog
        issued={issued}
        onClose={() => issued && done({ kind: 'partner', id: issued.id })}
      />
    </>
  );
}
